/**
 * Relationship Scenario Planner
 *
 * Drag-and-drop exploration tool that loads an item pool and surfaces
 * relationship constraints inline as you reorder.
 *
 * Two constraint types fire here:
 *   - Package atomicity: a Package's members must all be in scope (above the
 *     cut line) or all out of scope (below the cut line, or not in this pool).
 *     Adjacency is NOT a constraint.
 *   - Hard sequence ordering: dependent must rank after its predecessor
 *     (dependency + prerequisite edges, normalized).
 *
 * Intentionally ephemeral per Build Contract §2.2 — scenario ordering is
 * not written back to the DB. Relationship data driving constraints is
 * read from the existing itemRelationshipsStore (DB-backed).
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragStartEvent, DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AlertTriangle,
  ArrowDownUp,
  GripVertical,
  Link2,
  Package,
  RefreshCw,
  X,
  ArrowRight,
  Info,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useItemPoolsStore } from '../../../stores/prioritization/itemPoolsStore';
import { useCollectionsStore } from '../../../stores/prioritization/collectionsStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useItemRelationshipsStore } from '../../../stores/prioritization/itemRelationshipsStore';
import type {
  ItemRelationshipGroup,
  ItemRelationshipEdge,
} from '../../../types/prioritization/itemRelationships';
import { isHardSequenceEdge, normalizeSequencePair } from '../../../lib/sequenceRelationships';
import { Select } from '../../../components/ds';

type SourceType = 'pool' | 'collection';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Violation {
  id: string;
  type: 'package_split' | 'dependency_inversion';
  // For dependency inversions these point at the source/target of the inversion.
  // For package splits, they're representative in-pool members so existing UI that
  // reads movedItemId/relatedItemId still works; affectedItemIds is the full set.
  movedItemId: string;
  relatedItemId: string;
  affectedItemIds?: string[];
  groupTitle?: string;
  message: string;
  suggestion: string;
}

interface ScenarioItem {
  id: string;
  title: string;
  category?: string;
  packages: ItemRelationshipGroup[];
  hardDeps: ItemRelationshipEdge[];
  hardDependents: ItemRelationshipEdge[];
}

// ─── Constraint checker ───────────────────────────────────────────────────────

function checkViolations(
  order: string[],
  cutLine: number,
  items: ScenarioItem[],
  groupMemberships: { groupId: string; itemId: string }[],
  edges: ItemRelationshipEdge[],
): Violation[] {
  const violations: Violation[] = [];
  const inPool = new Set(order);

  // ── Package atomicity ────────────────────────────────────────────────────
  // A Package is "all-or-nothing for prioritization": every member must be
  // either in scope (above the cut line of this pool) or out of scope (below
  // the cut line, or not in this pool at all). Adjacency is NOT a constraint —
  // package members can sit anywhere in the ranking as long as scope agrees.
  const groupMembersById = new Map<string, string[]>();
  for (const m of groupMemberships) {
    const arr = groupMembersById.get(m.groupId);
    if (arr) arr.push(m.itemId);
    else groupMembersById.set(m.groupId, [m.itemId]);
  }

  for (const [groupId, memberIds] of groupMembersById) {
    if (memberIds.length < 2) continue;
    if (!memberIds.some(id => inPool.has(id))) continue;

    const aboveCut: string[] = [];
    const belowCut: string[] = [];
    const notInPool: string[] = [];
    for (const id of memberIds) {
      if (!inPool.has(id)) notInPool.push(id);
      else if (order.indexOf(id) < cutLine) aboveCut.push(id);
      else belowCut.push(id);
    }

    const hasInScope = aboveCut.length > 0;
    const hasOutOfScope = belowCut.length > 0 || notInPool.length > 0;
    if (!hasInScope || !hasOutOfScope) continue;

    const pkgTitle = items
      .find(i => i.packages.some(b => b.id === groupId))?.packages
      .find(b => b.id === groupId)?.title;

    let detail: string;
    if (belowCut.length > 0 && notInPool.length > 0) {
      detail = `${aboveCut.length} above the cut line, ${belowCut.length} below it, and ${notInPool.length} not in this pool`;
    } else if (belowCut.length > 0) {
      detail = `${aboveCut.length} above the cut line, ${belowCut.length} below`;
    } else {
      detail = `${aboveCut.length} in this pool, ${notInPool.length} not in this pool`;
    }

    const suggestion = belowCut.length > 0
      ? 'A package commits or defers as a unit — bring every member above the cut line, or move them all below.'
      : 'A package commits or defers as a unit — add the missing members to this pool, or remove the in-scope members.';

    violations.push({
      id: `pkg-${groupId}`,
      type: 'package_split',
      movedItemId: aboveCut[0],
      relatedItemId: belowCut[0] ?? notInPool[0] ?? aboveCut[0],
      affectedItemIds: [...aboveCut, ...belowCut],
      groupTitle: pkgTitle,
      message: `Package${pkgTitle ? ` "${pkgTitle}"` : ''} is split across scope — ${detail}.`,
      suggestion,
    });
  }

  // ── Hard sequence inversions (dependency + prerequisite, normalized) ─────
  // Dependent must rank after predecessor.
  const seenInversionKeys = new Set<string>();
  for (const edge of edges) {
    if (!(edge.status === 'active' || edge.status === 'confirmed')) continue;
    if (!isHardSequenceEdge(edge)) continue;
    const pair = normalizeSequencePair(edge);
    if (!pair) continue;
    if (!inPool.has(pair.predecessorItemId) || !inPool.has(pair.dependentItemId)) continue;
    if (order.indexOf(pair.dependentItemId) < order.indexOf(pair.predecessorItemId)) {
      const key = `${pair.dependentItemId}::${pair.predecessorItemId}`;
      if (seenInversionKeys.has(key)) continue;
      seenInversionKeys.add(key);
      const dep = items.find(i => i.id === pair.dependentItemId);
      const pred = items.find(i => i.id === pair.predecessorItemId);
      violations.push({
        id: `dep-${pair.edgeId}`,
        type: 'dependency_inversion',
        movedItemId: pair.dependentItemId,
        relatedItemId: pair.predecessorItemId,
        message: `"${dep?.title ?? pair.dependentItemId}" waits on "${pred?.title ?? pair.predecessorItemId}" but is now ranked above it.`,
        suggestion: `Move "${pred?.title ?? pair.predecessorItemId}" above this item to restore the correct order.`,
      });
    }
  }

  return violations;
}

// ─── Bloc color palette ───────────────────────────────────────────────────────

const BLOC_BORDER_COLORS = [
  'border-l-primary-400',
  'border-l-ocean-400',
  'border-l-violet-400',
  'border-l-terracotta-400',
  'border-l-electric-mustard-400',
] as const;

const BLOC_SWATCH_COLORS = [
  'bg-primary-400',
  'bg-ocean-400',
  'bg-violet-400',
  'bg-terracotta-400',
  'bg-electric-mustard-400',
] as const;

// ─── Sortable row ─────────────────────────────────────────────────────────────

function SortableRow({
  item,
  rank,
  cutLine,
  isActive,
  isSelected,
  isHighlighted,
  hasViolation,
  packageBorderColor,
  onSelect,
}: {
  item: ScenarioItem;
  rank: number;
  cutLine: number;
  isActive: boolean;
  isSelected: boolean;
  isHighlighted: boolean;
  hasViolation: boolean;
  packageBorderColor: string;
  onSelect: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
  };

  const aboveLine = rank <= cutLine;

  let rowClass =
    'flex items-center gap-3 px-3 py-2.5  border transition-all duration-150 cursor-pointer ';

  if (hasViolation) {
    rowClass += 'bg-[var(--coral-dim)] border-[var(--coral)] ';
  } else if (isActive || isSelected) {
    rowClass += 'bg-primary-50 border-primary-300 shadow-sm ring-2 ring-primary-200 ';
  } else if (isHighlighted) {
    rowClass += 'bg-[var(--mustard-dim)] border-[var(--mustard)] ';
  } else if (aboveLine) {
    rowClass += 'bg-[var(--bg-raised)] border-[var(--rule)] hover:bg-[var(--surface)] ';
  } else {
    rowClass += 'bg-[var(--surface)] border-[var(--rule)] opacity-75 hover:opacity-100 ';
  }

  if (item.packages.length > 0) {
    rowClass += `border-l-4 ${packageBorderColor} `;
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={rowClass.trim()}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
        {/* Drag handle — DnD listeners are scoped to this button so a row
            click does not start a drag. */}
        <button
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          className="flex-shrink-0 cursor-grab active:cursor-grabbing text-[var(--text-ter)] hover:text-[var(--text-sec)] touch-none"
          aria-label="Drag to reorder"
        >
          <GripVertical className="h-4 w-4" />
        </button>

        {/* Rank */}
        <span
          className={`flex-shrink-0 w-6 text-center text-xs font-bold tabular-nums ${
            aboveLine ? 'text-[var(--text-sec)]' : 'text-[var(--text-ter)]'
          }`}
        >
          {rank}
        </span>

        {/* Title + category */}
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium truncate ${aboveLine ? 'text-[var(--text-pri)]' : 'text-[var(--text-ter)]'}`}>
            {item.title}
          </p>
          {item.category && (
            <p className="text-xs text-[var(--text-ter)] mt-0.5 truncate capitalize">{item.category}</p>
          )}
        </div>

        {/* Badges */}
        <div className="flex-shrink-0 flex items-center gap-1.5">
          {item.packages.map(pkg => (
            <span
              key={pkg.id}
              title={`Package: ${pkg.title ?? 'unnamed'}`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-primary-100 text-primary-700"
            >
              <Package className="h-3 w-3" />
              {pkg.title ?? 'Package'}
            </span>
          ))}
          {item.hardDeps.length > 0 && (
            <span
              title={`Depends on ${item.hardDeps.length} item(s)`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-[var(--mustard-dim)] text-[var(--mustard)]"
            >
              <Link2 className="h-3 w-3" />
              dep
            </span>
          )}
          {item.hardDependents.length > 0 && (
            <span
              title={`${item.hardDependents.length} item(s) depend on this`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-[var(--slate-dim)] text-[var(--text-sec)]"
            >
              <ArrowRight className="h-3 w-3" />
              req'd by
            </span>
          )}
          {hasViolation && <AlertTriangle className="h-4 w-4 text-[var(--coral)] flex-shrink-0" />}
          {isHighlighted && !hasViolation && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-[var(--mustard-dim)] text-[var(--mustard)]">
              related
            </span>
          )}
        </div>
      </div>
  );
}

// ─── Cut-line divider (draggable) ─────────────────────────────────────────────

const CUTLINE_ID = '__cutline__';

function CutLineDivider({ cutLine, isDragging }: { cutLine: number; isDragging: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: CUTLINE_ID,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      role="separator"
      aria-label="Cut line — drag to change scope"
      title="Drag to change scope"
      className={`flex items-center gap-2 py-1 px-1 cursor-grab active:cursor-grabbing select-none touch-none ${
        isDragging ? 'z-10' : ''
      }`}
    >
      <div className="flex-1 border-t-2 border-dashed border-[var(--coral)]" />
      <div
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 shadow-sm transition-colors ${
          isDragging
            ? 'bg-[var(--coral-dim)] border-[var(--coral)]'
            : 'bg-[var(--bg-raised)] border-[var(--coral)] hover:bg-[var(--coral-dim)] hover:border-[var(--coral)]'
        }`}
      >
        <GripVertical className="h-3.5 w-3.5 text-[var(--coral)]" />
        <span className="text-xs font-bold uppercase tracking-wider whitespace-nowrap text-[var(--coral)]">
          cut line · {cutLine} in scope
        </span>
      </div>
      <div className="flex-1 border-t-2 border-dashed border-[var(--coral)]" />
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function RelationshipScenarioPage() {
  const itemPools = useItemPoolsStore(s => s.itemPools);
  const fetchPools = useItemPoolsStore(s => s.fetchFromSupabase);
  const collections = useCollectionsStore(s => s.collections);
  const fetchCollections = useCollectionsStore(s => s.fetchFromSupabase);
  const getOrderedItemIdsForCollection = useCollectionsStore(s => s.getOrderedItemIdsForCollection);
  const getItemById = useItemsStore(s => s.getItemById);
  const fetchItems = useItemsStore(s => s.fetchFromSupabase);
  const fetchRelationships = useItemRelationshipsStore(s => s.fetchFromSupabase);
  const groups = useItemRelationshipsStore(s => s.groups);
  const groupMemberships = useItemRelationshipsStore(s => s.groupMemberships);
  const edges = useItemRelationshipsStore(s => s.edges);

  const [sourceType, setSourceType] = useState<SourceType>('pool');
  const [selectedPoolId, setSelectedPoolId] = useState('');
  const [selectedCollectionId, setSelectedCollectionId] = useState('');
  const [localOrder, setLocalOrder] = useState<string[]>([]);
  const [cutLine, setCutLine] = useState(0);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [violations, setViolations] = useState<Violation[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  // Cold-load: this page can be entered directly without going through the
  // dashboard, so we have to fetch our own data instead of assuming it's
  // already in the stores.
  useEffect(() => {
    void fetchRelationships();
    void fetchPools();
    void fetchCollections();
    void fetchItems?.();
  }, [fetchRelationships, fetchPools, fetchCollections, fetchItems]);

  useEffect(() => {
    if (!selectedPoolId && itemPools.length > 0) setSelectedPoolId(itemPools[0].id);
  }, [itemPools, selectedPoolId]);

  useEffect(() => {
    if (!selectedCollectionId && collections.length > 0) setSelectedCollectionId(collections[0].id);
  }, [collections, selectedCollectionId]);

  const selectedPool = useMemo(
    () => itemPools.find(p => p.id === selectedPoolId),
    [itemPools, selectedPoolId],
  );

  const selectedCollection = useMemo(
    () => collections.find(c => c.id === selectedCollectionId),
    [collections, selectedCollectionId],
  );

  // Load items when the pool selection changes
  useEffect(() => {
    if (sourceType !== 'pool') return;
    if (selectedPool) {
      setLocalOrder([...selectedPool.priorityIds]);
      setCutLine(selectedPool.cutLinePosition);
      setDismissedIds(new Set());
      setSelectedId(null);
    }
  }, [selectedPool, sourceType]);

  // Load items when the collection selection changes
  useEffect(() => {
    if (sourceType !== 'collection') return;
    if (selectedCollection) {
      setLocalOrder(getOrderedItemIdsForCollection(selectedCollection.id));
      setCutLine(0);
      setDismissedIds(new Set());
      setSelectedId(null);
    }
  }, [selectedCollection, sourceType, getOrderedItemIdsForCollection]);

  // Reset local state when switching source type
  useEffect(() => {
    setLocalOrder([]);
    setCutLine(0);
    setDismissedIds(new Set());
    setSelectedId(null);
    setViolations([]);
  }, [sourceType]);

  const scenarioItems = useMemo<ScenarioItem[]>(() => {
    return localOrder.map(id => {
      const item = getItemById(id);
      const memberGroupIds = new Set(
        groupMemberships.filter(m => m.itemId === id).map(m => m.groupId),
      );
      const packages = groups.filter(
        g => memberGroupIds.has(g.id) && g.type === 'package' &&
          (g.status === 'active' || g.status === 'confirmed'),
      );
      const hardDeps = edges.filter((e) => {
        if (!(e.status === 'active' || e.status === 'confirmed')) return false;
        if (!isHardSequenceEdge(e)) return false;
        const pair = normalizeSequencePair(e);
        return pair?.dependentItemId === id;
      });
      const hardDependents = edges.filter((e) => {
        if (!(e.status === 'active' || e.status === 'confirmed')) return false;
        if (!isHardSequenceEdge(e)) return false;
        const pair = normalizeSequencePair(e);
        return pair?.predecessorItemId === id;
      });
      return {
        id,
        title: item?.title ?? id,
        category: (item as { category?: string })?.category,
        packages,
        hardDeps,
        hardDependents,
      };
    });
  }, [localOrder, getItemById, groups, groupMemberships, edges]);

  const packageColorMap = useMemo(() => {
    const map = new Map<string, { border: string; swatch: string }>();
    let i = 0;
    for (const g of groups.filter(g => g.type === 'package')) {
      map.set(g.id, {
        border: BLOC_BORDER_COLORS[i % BLOC_BORDER_COLORS.length],
        swatch: BLOC_SWATCH_COLORS[i % BLOC_SWATCH_COLORS.length],
      });
      i++;
    }
    return map;
  }, [groups]);

  // Highlight related items either while dragging (activeId) OR after a click
  // selection (selectedId). Clicking gives users a no-commit way to ask
  // "what relates to this item?" without having to start a drag.
  const focusedId = activeId ?? selectedId;
  const highlightedIds = useMemo<Set<string>>(() => {
    if (!focusedId) return new Set();
    const focused = scenarioItems.find(i => i.id === focusedId);
    if (!focused) return new Set();
    const ids = new Set<string>();
    for (const pkg of focused.packages) {
      groupMemberships
        .filter(m => m.groupId === pkg.id && m.itemId !== focusedId)
        .forEach(m => ids.add(m.itemId));
    }
    focused.hardDeps.forEach((e) => {
      const pair = normalizeSequencePair(e);
      if (pair) ids.add(pair.predecessorItemId);
    });
    focused.hardDependents.forEach((e) => {
      const pair = normalizeSequencePair(e);
      if (pair) ids.add(pair.dependentItemId);
    });
    return ids;
  }, [focusedId, scenarioItems, groupMemberships]);

  const violationItemIds = useMemo(() => {
    const ids = new Set<string>();
    for (const v of violations) {
      if (dismissedIds.has(v.id)) continue;
      if (v.affectedItemIds && v.affectedItemIds.length > 0) {
        for (const id of v.affectedItemIds) ids.add(id);
      } else {
        ids.add(v.movedItemId);
        ids.add(v.relatedItemId);
      }
    }
    return ids;
  }, [violations, dismissedIds]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // The DnD sortable list contains both the items AND the cut-line sentinel,
  // so the same drag interaction can either reorder items OR move the cut line.
  const sortableIds = useMemo(() => {
    const ids: string[] = [];
    localOrder.forEach((id, idx) => {
      if (idx === cutLine) ids.push(CUTLINE_ID);
      ids.push(id);
    });
    if (cutLine >= localOrder.length) ids.push(CUTLINE_ID);
    return ids;
  }, [localOrder, cutLine]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    setDismissedIds(new Set());
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);
      if (!over || active.id === over.id) return;
      const oldIdx = sortableIds.indexOf(active.id as string);
      const newIdx = sortableIds.indexOf(over.id as string);
      if (oldIdx < 0 || newIdx < 0) return;
      const reordered = arrayMove(sortableIds, oldIdx, newIdx);
      const cutIdx = reordered.indexOf(CUTLINE_ID);
      const nextOrder = reordered.filter(id => id !== CUTLINE_ID);
      setLocalOrder(nextOrder);
      // The cut line's position in the sentinel-included list IS the count of
      // items above it, which is exactly what `cutLine` represents.
      setCutLine(cutIdx);
    },
    [sortableIds],
  );

  // Re-check violations whenever the order, cut line, or relationship data changes.
  // This means existing violations show as soon as a pool is loaded — the user
  // doesn't have to drag something first to discover that the data is broken.
  useEffect(() => {
    if (localOrder.length === 0) {
      setViolations([]);
      return;
    }
    setViolations(checkViolations(localOrder, cutLine, scenarioItems, groupMemberships, edges));
  }, [localOrder, cutLine, scenarioItems, groupMemberships, edges]);

  const reset = useCallback(() => {
    if (sourceType === 'pool' && selectedPool) {
      setLocalOrder([...selectedPool.priorityIds]);
      setCutLine(selectedPool.cutLinePosition);
      setDismissedIds(new Set());
      setSelectedId(null);
    } else if (sourceType === 'collection' && selectedCollection) {
      setLocalOrder(getOrderedItemIdsForCollection(selectedCollection.id));
      setCutLine(0);
      setDismissedIds(new Set());
      setSelectedId(null);
    }
  }, [sourceType, selectedPool, selectedCollection, getOrderedItemIdsForCollection]);

  // Bring all in-pool members of a split package into scope by reordering them
  // immediately above the cut line, preserving each member's relative order.
  const includeWholePackage = useCallback(
    (v: Violation) => {
      if (!v.affectedItemIds || v.affectedItemIds.length === 0) return;
      const affected = v.affectedItemIds.filter(id => localOrder.includes(id));
      if (affected.length === 0) return;
      const remaining = localOrder.filter(id => !affected.includes(id));
      const inOriginalOrder = affected.sort(
        (a, b) => localOrder.indexOf(a) - localOrder.indexOf(b),
      );
      const insertAt = Math.min(cutLine - inOriginalOrder.length, remaining.length);
      const safeInsert = Math.max(0, insertAt);
      const newOrder = [
        ...remaining.slice(0, safeInsert),
        ...inOriginalOrder,
        ...remaining.slice(safeInsert),
      ];
      setLocalOrder(newOrder);
      setDismissedIds(new Set());
    },
    [localOrder, cutLine],
  );

  // Push all in-pool members of a split package out of scope (immediately below
  // the cut line), preserving each member's relative order.
  const excludeWholePackage = useCallback(
    (v: Violation) => {
      if (!v.affectedItemIds || v.affectedItemIds.length === 0) return;
      const affected = v.affectedItemIds.filter(id => localOrder.includes(id));
      if (affected.length === 0) return;
      const remaining = localOrder.filter(id => !affected.includes(id));
      const inOriginalOrder = affected.sort(
        (a, b) => localOrder.indexOf(a) - localOrder.indexOf(b),
      );
      const insertAt = Math.min(cutLine, remaining.length);
      const newOrder = [
        ...remaining.slice(0, insertAt),
        ...inOriginalOrder,
        ...remaining.slice(insertAt),
      ];
      setLocalOrder(newOrder);
      setDismissedIds(new Set());
    },
    [localOrder, cutLine],
  );

  const activeViolations = violations.filter(v => !dismissedIds.has(v.id));
  const packagesInPool = groups.filter(
    g => g.type === 'package' &&
      (g.status === 'active' || g.status === 'confirmed') &&
      groupMemberships.some(m => m.groupId === g.id && localOrder.includes(m.itemId)),
  );

  return (
    <div className="space-y-6 pb-12">
      <PageHeader
        icon={<ArrowDownUp className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Relationship Scenario Planner"
        subtitle="Explore what-if orderings and see relationship constraints surface in real time"
        actions={
          <button
            onClick={reset}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Reset
          </button>
        }
      />

      {/* Exploration mode notice */}
      <div className="flex items-start gap-3 px-4 py-3 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
        <Info className="h-4 w-4 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
        <p className="text-sm text-[var(--mustard)]">
          <strong>Exploration mode.</strong> Drag to try different orderings. Relationship
          constraints surface automatically. The published ranking is not affected.
        </p>
      </div>

      {/* Source selector + legend */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {/* Source type toggle */}
          <div className="flex border border-[var(--rule)] overflow-hidden text-sm">
            <button
              type="button"
              onClick={() => setSourceType('pool')}
              className={`px-3 py-2 font-medium transition-colors ${
                sourceType === 'pool'
                  ? 'bg-primary-50 text-primary-700 border-r border-[var(--rule)]'
                  : 'bg-[var(--bg-raised)] text-[var(--text-sec)] hover:bg-[var(--surface)] border-r border-[var(--rule)]'
              }`}
            >
              Item Pool
            </button>
            <button
              type="button"
              onClick={() => setSourceType('collection')}
              className={`px-3 py-2 font-medium transition-colors ${
                sourceType === 'collection'
                  ? 'bg-primary-50 text-primary-700'
                  : 'bg-[var(--bg-raised)] text-[var(--text-sec)] hover:bg-[var(--surface)]'
              }`}
            >
              Collection
            </button>
          </div>

          {/* Item Pool selector */}
          {sourceType === 'pool' && (
            <div className="w-64">
              <Select
                value={selectedPoolId}
                onChange={e => setSelectedPoolId(e.target.value)}
                aria-label="Item pool"
              >
                {itemPools.length === 0 && <option value="">No item pools found</option>}
                {itemPools.map(pool => (
                  <option key={pool.id} value={pool.id}>{pool.name}</option>
                ))}
              </Select>
            </div>
          )}

          {/* Collection selector */}
          {sourceType === 'collection' && (
            <div className="w-64">
              <Select
                value={selectedCollectionId}
                onChange={e => setSelectedCollectionId(e.target.value)}
                aria-label="Collection"
              >
                {collections.length === 0 && <option value="">No collections found</option>}
                {collections.map(col => (
                  <option key={col.id} value={col.id}>{col.name}</option>
                ))}
              </Select>
            </div>
          )}
        </div>

        <div className="flex items-center gap-4 flex-wrap">
          <span className="flex items-center gap-1 text-xs text-[var(--text-ter)]">
            <Package className="h-3.5 w-3.5 text-primary-500" /> Package
          </span>
          <span className="flex items-center gap-1 text-xs text-[var(--text-ter)]">
            <Link2 className="h-3.5 w-3.5 text-[var(--mustard)]" /> Depends on
          </span>
          <span className="flex items-center gap-1 text-xs text-[var(--text-ter)]">
            <ArrowRight className="h-3.5 w-3.5 text-[var(--text-ter)]" /> Required by
          </span>
          <span className="flex items-center gap-1 text-xs text-[var(--text-ter)]">
            <AlertTriangle className="h-3.5 w-3.5 text-[var(--coral)]" /> Constraint violated
          </span>
        </div>
      </div>

      {/* Violation panels */}
      {activeViolations.length > 0 && (
        <div className="space-y-2">
          {activeViolations.map(v => (
            <div
              key={v.id}
              className={`flex items-start gap-3 px-4 py-3  border ${
                v.type === 'package_split'
                  ? 'bg-[var(--mustard-dim)] border-[var(--mustard)]'
                  : 'bg-[var(--coral-dim)] border-[var(--coral)]'
              }`}
            >
              <AlertTriangle
                className={`h-4 w-4 flex-shrink-0 mt-0.5 ${
                  v.type === 'package_split' ? 'text-[var(--mustard)]' : 'text-[var(--coral)]'
                }`}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[var(--text-pri)]">
                  {v.type === 'package_split' ? 'Package split across scope' : 'Out-of-sequence dependency'}
                </p>
                <p className="text-sm text-[var(--text-sec)] mt-0.5">{v.message}</p>
                <p className="text-xs text-[var(--text-ter)] mt-1">{v.suggestion}</p>
                {v.type === 'package_split' && v.affectedItemIds && v.affectedItemIds.length > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => includeWholePackage(v)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-[var(--mustard)] bg-[var(--mustard-dim)] border border-[var(--mustard)] hover:bg-[var(--mustard-dim)] transition-colors"
                    >
                      <Package className="h-3 w-3" />
                      Pull whole package above the cut line
                    </button>
                    <button
                      onClick={() => excludeWholePackage(v)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors"
                    >
                      Push whole package below the cut line
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={() => setDismissedIds(prev => new Set([...prev, v.id]))}
                className="flex-shrink-0 text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {localOrder.length === 0 && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-12 flex flex-col items-center justify-center text-[var(--text-ter)]">
          <ArrowDownUp className="h-10 w-10 mb-3 opacity-40" />
          <p className="text-sm">
            {sourceType === 'pool'
              ? itemPools.length === 0
                ? 'No item pools found. Publish a session to create one.'
                : 'Selected pool has no items.'
              : collections.length === 0
                ? 'No collections found. Create a collection and add items to it.'
                : 'Selected collection has no items.'}
          </p>
        </div>
      )}

      {/* Ranked list */}
      {localOrder.length > 0 && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
              <div className="space-y-1.5">
                {sortableIds.map(id => {
                  if (id === CUTLINE_ID) {
                    return (
                      <CutLineDivider
                        key={CUTLINE_ID}
                        cutLine={cutLine}
                        isDragging={activeId === CUTLINE_ID}
                      />
                    );
                  }
                  const item = scenarioItems.find(i => i.id === id);
                  if (!item) return null;
                  const idx = localOrder.indexOf(id);
                  const packageId = item.packages[0]?.id;
                  const borderColor = packageId
                    ? (packageColorMap.get(packageId)?.border ?? 'border-l-primary-400')
                    : '';
                  return (
                    <SortableRow
                      key={item.id}
                      item={item}
                      rank={idx + 1}
                      cutLine={cutLine}
                      isActive={activeId === item.id}
                      isSelected={selectedId === item.id}
                      isHighlighted={highlightedIds.has(item.id)}
                      hasViolation={violationItemIds.has(item.id)}
                      packageBorderColor={borderColor}
                      onSelect={() => setSelectedId(prev => (prev === item.id ? null : item.id))}
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}

      {/* Package key */}
      {packagesInPool.length > 0 && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-3">
            Packages in this pool
          </p>
          <div className="space-y-2">
            {packagesInPool.map(g => {
              const memberTitles = groupMemberships
                .filter(m => m.groupId === g.id && localOrder.includes(m.itemId))
                .map(m => scenarioItems.find(i => i.id === m.itemId)?.title ?? m.itemId);
              const colors = packageColorMap.get(g.id);
              return (
                <div key={g.id} className="flex items-start gap-2 text-sm">
                  <div className={`mt-1 w-2 h-4  flex-shrink-0 ${colors?.swatch ?? 'bg-primary-400'}`} />
                  <div className="min-w-0">
                    <span className="font-medium text-[var(--text-pri)]">{g.title ?? 'Unnamed package'}</span>
                    <span className="text-[var(--text-ter)] mx-1.5">—</span>
                    <span className="text-[var(--text-sec)]">{memberTitles.join(' · ')}</span>
                    {g.rationale && (
                      <p className="text-xs text-[var(--text-ter)] italic mt-0.5">"{g.rationale}"</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
